import React, { createContext, useContext, useEffect, useState } from 'react';
import io from 'socket.io-client';
import { useUser } from './UserContext';

const SocketContext = createContext();

export const useSocket = () => {
    return useContext(SocketContext);
};

// Singleton instances for multiplexing
let chatSocketInstance = null;
let notificationSocketInstance = null;

export const SocketProvider = ({ children }) => {
    const [chatSocket, setChatSocket] = useState(null);
    const [notificationSocket, setNotificationSocket] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const { profileData } = useUser();

    const apiBaseForSocket =
        process.env.REACT_APP_API_URL || 'http://localhost:8890/api/v1';
    
    // Clean URL for socket - strip /api/v1 and trailing slashes
    const SOCKET_BASE_URL =
        process.env.REACT_APP_SOCKET_URL ||
        apiBaseForSocket.replace(/\/?api\/v1\/?$/, '').replace(/\/$/, '') ||
        'http://localhost:8890';

    useEffect(() => {
        const token = localStorage.getItem("token");

        // Only connect if user is logged in and token exists
        if (profileData && profileData._id && token) {
            
            // 1. Initialize Chat Namespace Socket
            if (!chatSocketInstance) {
                console.log("🚀 Initializing Chat Socket...");
                chatSocketInstance = io(`${SOCKET_BASE_URL}/chat`, {
                    auth: { token },
                    transports: ['websocket', 'polling'],
                    reconnection: true,
                    reconnectionAttempts: 10,
                    reconnectionDelay: 1000,
                });

                chatSocketInstance.on('connect', () => {
                    console.log('✅ Chat Socket connected:', chatSocketInstance.id);
                    setIsConnected(true);
                    
                    // The backend handles presence and room joining automatically 
                    // if it uses socket.data.userId from the auth handshake.
                    // But we can still emit a join to be safe if required.
                    const uid = profileData._id.toString();
                    chatSocketInstance.emit('join', uid);
                });

                chatSocketInstance.on('disconnect', () => {
                    console.log('❌ Chat Socket disconnected');
                    // Only set isConnected to false if both are disconnected
                    if (!notificationSocketInstance?.connected) {
                        setIsConnected(false);
                    }
                });

                chatSocketInstance.on('connect_error', (err) => {
                    console.error('⚠️ Chat Socket connection error:', err.message);
                });
            }

            // 2. Initialize Notification Namespace Socket
            if (!notificationSocketInstance) {
                console.log("🚀 Initializing Notification Socket...");
                notificationSocketInstance = io(`${SOCKET_BASE_URL}/notifications`, {
                    auth: { token },
                    transports: ['websocket', 'polling'],
                    reconnection: true,
                    reconnectionAttempts: 10,
                });

                notificationSocketInstance.on('connect', () => {
                    console.log('✅ Notification Socket connected:', notificationSocketInstance.id);
                    
                    // Join notification and course rooms
                    const uid = profileData._id.toString();
                    notificationSocketInstance.emit('joinNotificationRoom', uid);

                    if (profileData.purchasedCourses && profileData.purchasedCourses.length > 0) {
                        const courseIds = profileData.purchasedCourses
                            .filter(pc => {
                                // Skip explicitly suspended or paused courses to prevent backend rejection logs
                                if (pc.accessSuspended) return false;
                                if (pc.assignedByAdmin && pc.assignedByAdmin.status === "PAUSED") return false;
                                if (pc.expiresAt && new Date(pc.expiresAt) < new Date()) return false;
                                return true;
                            })
                            .map(pc => (pc.course?._id || pc.course)?.toString())
                            .filter(id => id);

                        if (courseIds.length > 0) {
                            notificationSocketInstance.emit('joinCourseRooms', { courseIds });
                        }
                    }
                });

                notificationSocketInstance.on('connect_error', (err) => {
                    console.error('⚠️ Notification Socket connection error:', err.message);
                });
            }

            setChatSocket(chatSocketInstance);
            setNotificationSocket(notificationSocketInstance);

        } else {
            // Cleanup on logout
            if (chatSocketInstance) {
                chatSocketInstance.disconnect();
                chatSocketInstance = null;
            }
            if (notificationSocketInstance) {
                notificationSocketInstance.disconnect();
                notificationSocketInstance = null;
            }
            setChatSocket(null);
            setNotificationSocket(null);
            setIsConnected(false);
        }

    }, [profileData, SOCKET_BASE_URL]);

    return (
        <SocketContext.Provider value={{ 
            socket: chatSocket, // Default backward compatibility
            chatSocket, 
            notificationSocket, 
            isConnected 
        }}>
            {children}
        </SocketContext.Provider>
    );
};
